import { ReactNode, useMemo } from 'react';
import { AccessibilityText } from '@glu/utilities-react';
import locale from '@glu/locale';
import { darkModeAware } from '@glu/theming';
import classnames from 'classnames';
import { InfoCircleAshIcon } from '@glu/icons-react';
import { Tooltip } from '@glu/tooltip-react';
import useStyles from './LabelText.styles';

export interface ILabelTextProps {
  /** Additional class to add to label */
  className?: string;
  /** Indicates if the label is used for a checkbox */
  checkboxLabel?: boolean;
  /** Indicates dark mode */
  dark?: boolean;
  /** Add data-qa to the label */
  dataQa?: string;
  /** Shows LabelText in red if true (used in styles) */
  hasError?: boolean;
  /** Optional id attribute to add to element */
  htmlId?: string;
  /** Indicates whether or not you want the standard inline layout */
  inline?: boolean;
  /** Label text to use for label */
  label: ReactNode;
  /** Shows optional text on label */
  optional?: boolean;
  /** Determines if this label should be screen reader only */
  screenReaderLabel?: boolean;
  /**
   * Content that is displayed in tooltip when hovering over the
   * icon that the tooltip wraps.
   */
  tooltipContent?: ReactNode;
  /** Applies warning styles to the element */
  warning?: boolean;
}

const LabelText = ({
  checkboxLabel,
  className,
  dark,
  dataQa,
  hasError,
  htmlId,
  inline,
  label,
  optional,
  screenReaderLabel,
  tooltipContent,
  warning,
}: ILabelTextProps) => {
  const classes = useStyles();
  const optionalText = useMemo(() => {
    if (optional && !inline) {
      return (
        <span
          className={classnames(classes.optional, {
            [classes.optionalWithTooltip]: !!tooltipContent,
          })}
        >
          {locale.get('optional')}
        </span>
      );
    }

    return null;
  }, [classes.optional, classes.optionalWithTooltip, inline, optional, tooltipContent]);

  if (screenReaderLabel) {
    return <AccessibilityText>{label}</AccessibilityText>;
  }

  return (
    <div
      className={classnames(
        checkboxLabel ? classes.checkboxLabelWrapper : classes.root,
        {
          [classes.dark]: dark,
          [classes.inline]: inline,
          [classes.warning]: warning,
          [classes.error]: hasError,
        },
        className
      )}
      data-qa={dataQa}
      id={htmlId}
    >
      <p className={checkboxLabel ? classes.checkboxLabel : classes.inlineText}>{label}</p>
      {tooltipContent && (
        <Tooltip className={classes.toolTip} content={tooltipContent} placement="right">
          <InfoCircleAshIcon
            className={checkboxLabel ? classes.toolTipIconCheckbox : classes.toolTipIcon}
            data-qa="labeltext-tooltip"
          />
        </Tooltip>
      )}
      {optionalText}
    </div>
  );
};

export const BareLabelText = LabelText;
export default darkModeAware(LabelText);
